import { formatArtists, pickI18n, type SongDTO } from '@akpl/shared';
import {
  BackPriority,
  Button,
  cx,
  doesFocusableExist,
  FocusContext,
  getCurrentFocusKey,
  IconQr,
  QrCode,
  QueueRow,
  setFocus,
  Tag,
  useBackHandler,
  useFocusable,
} from '@akpl/ui-tv';
import { useEffect, useMemo, useRef, useState } from 'react';

import { colorOf } from '../../dev/placeholders';
import { GUEST_URL, useJoinQr } from '../../queue/useJoinQr';
import { useQueueActions } from '../../queue/useQueueActions';
import { useRouter } from '../../router';
import { queueEntries, type QueueEntry, selectQueuedCount, selectSid, useStore } from '../../store';
import { useT } from '../../useT';
import styles from './QueueDrawer.module.css';

export const DRAWER_FOCUS_KEY = 'queue-drawer';
export const DRAWER_QR_FOCUS = 'drawer-qr';
export const DRAWER_SEARCH_FOCUS = 'drawer-search';
export const DRAWER_CLOSE_FOCUS = 'drawer-close';
export const drawerRowKey = (index: number) => `drawer-row-${index}`;
/**
 * UX-312: the "Ya cantadas" rows are deliberately not navigable (`focusable: false` on a played
 * row), but they were rendered without a key at all — so norigin registered each of them under
 * its own fallback name (`sn:focusable-item-1`) and the drawer reported a focusable the remote
 * could never reach. They are keyed now: still not a stop, but a stable, nameable one.
 */
export const drawerPlayedKey = (index: number) => `drawer-played-${index}`;
/** UX-306: the ids the drawer is named and described by. */
export const DRAWER_TITLE_ID = 'queue-drawer-title';
export const DRAWER_DESC_ID = 'queue-drawer-count';

/** Figma TV-05 68:526: 552 px drawer, 92 px rows 10 px apart under a 56 px header. */
export const DRAWER_HEIGHT = 552;
const ROW_H = 92;
const ROW_GAP = 10;
const STRIDE = ROW_H + ROW_GAP;
export const LIST_VIEWPORT = 442;

/**
 * Vertical lists scroll only when the focused row would leave the viewport (the same rule as
 * the playlist list); the horizontal rows are the ones that pin the focus to the leading edge.
 */
export function scrollFor(index: number, offset: number, count: number): number {
  const max = Math.max(0, count * STRIDE - ROW_GAP - LIST_VIEWPORT);
  const top = index * STRIDE;
  const bottom = top + ROW_H;
  const next =
    top < offset ? top : bottom > offset + LIST_VIEWPORT ? bottom - LIST_VIEWPORT : offset;
  return Math.max(0, Math.min(next, max));
}

/**
 * A queue entry already sung, as the song the action dialog works with. The RTDB item carries
 * only what the drawer draws (title, artists as one string, cover, tone), so the rest is the
 * neutral default: the dialog needs the id, the display fields and the tone to preselect — the
 * five tone chips are fixed (docs/07 §1) and the API validates the song on the request.
 */
function songOfEntry(entry: QueueEntry): SongDTO {
  return {
    id: entry.songId,
    title: entry.title,
    artists: entry.artistNames ? [{ id: entry.songId, name: entry.artistNames }] : [],
    imageUrl: entry.imageUrl ?? null,
    genre: null,
    tier: 'standard',
    tones: [
      {
        name: entry.tone,
        label: { en: `Key ${entry.tone}`, es: `Tono ${entry.tone}` },
        semitones: Number(entry.tone) || 0,
        isDefault: true,
      },
    ],
    durationMs: null,
    hasLyrics: false,
    locked: false,
  };
}

export interface QueueDrawerProps {
  /** Switch the player to this queue item (drawer "Reproducir"). */
  onPlayItem: (entry: QueueEntry) => void;
  onClose: () => void;
  /**
   * Enter on an "Ya cantadas" row: open the song action dialog for it (tone chips, play now /
   * add to queue). The dialog lives in the Player, not here — the drawer clips its children.
   */
  onSongAgain: (song: SongDTO) => void;
  /** Opens the join-QR modal shared with the HUD. */
  onShowQr: () => void;
  /**
   * Show the empty state whatever the queue node still says (UX-302): the last song's media
   * failed and the player has ended it locally, which the RTDB echo may not have caught up
   * with yet. Without this the room would read a row that can never play as "still to come".
   */
  forceEmpty?: boolean;
}

/**
 * The queue, as a bottom drawer over the video (docs/04 §7.6). It replaces the standalone
 * Queue screen: the rail's "Fila" item and the HUD's "N en fila" button both open it, and
 * Back closes it (a second Back leaves the player). Rows come from the live RTDB `queue/`
 * node mirrored in the store (WP1.6), sorted by `order`.
 */
export function QueueDrawer({
  onPlayItem,
  onClose,
  onSongAgain,
  onShowQr,
  forceEmpty = false,
}: QueueDrawerProps) {
  const { t, i18n } = useT();
  const locale = i18n.language === 'en' ? 'en' : 'es';
  const queue = useStore((s) => s.queue);
  const queuedCount = useStore(selectQueuedCount);
  const joinCode = useStore((s) => s.meta?.joinCode ?? s.session?.joinCode ?? null);
  const sid = useStore(selectSid);
  const joinUrl = useJoinQr(sid, joinCode);
  const reset = useRouter((s) => s.reset);
  const [offset, setOffset] = useState(0);

  const { active, played } = useMemo(() => {
    const entries = queueEntries(queue);
    return {
      active: entries.filter((e) => e.status !== 'played'),
      played: entries.filter((e) => e.status === 'played'),
    };
  }, [queue]);
  /**
   * "Empty" means nothing left to sing (docs/04 §4.6/§4.9). The items already sung stay in the
   * RTDB queue as `status: 'played'`, so counting them kept a drained queue on the "Ya cantadas"
   * list and the join QR never appeared when it is most needed — right after the last song
   * (QA TV-26).
   */
  const empty = forceEmpty || active.length === 0;

  const actions = useQueueActions({
    onPlayed: (itemId) => {
      const entry = active.find((e) => e.id === itemId);
      if (entry) onPlayItem(entry);
    },
  });

  const { ref, focusKey } = useFocusable<HTMLDivElement>({
    focusKey: DRAWER_FOCUS_KEY,
    trackChildren: true,
    saveLastFocusedChild: true,
    isFocusBoundary: true,
  });

  useBackHandler(
    () => {
      onClose();
      return true;
    },
    { priority: BackPriority.overlay },
  );

  /**
   * Always land on something (the first row, or the empty state's primary button) — and give
   * the focus back to whatever opened the drawer when it closes.
   *
   * UX-305: opened from the player HUD the restore was right because the player set it by hand;
   * opened from the Home rail it was not, and Escape dropped the user on `hero-play` — the top
   * of the content column, several presses from the rail they had been walking. The opener is
   * remembered here instead, so every way in gets the same way out.
   */
  const opener = useRef<string | null>(null);
  useEffect(() => {
    opener.current = getCurrentFocusKey() ?? null;
    void setFocus(empty ? DRAWER_SEARCH_FOCUS : active[0] ? drawerRowKey(0) : DRAWER_QR_FOCUS);
    return () => {
      const previous = opener.current;
      if (previous && doesFocusableExist(previous)) void setFocus(previous);
    };
    // Only on open; moving inside the drawer must not pull the focus back.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const rowCount = empty ? 0 : active.length + (played.length ? 1 + played.length : 0);
  const onRowFocus = (index: number) => setOffset((o) => scrollFor(index, o, rowCount));

  /**
   * UX-312 / UX-346: the drawer's own buttons could not be reached with the D-pad.
   *
   * "Agregar con QR" sits in the header at x 1515–1872 and every row spans x 48–1872 underneath
   * it, so norigin's spatial pick from a row never prefers the button above — and in the empty
   * state "Cerrar" sat beside "Buscar canciones" with the QR button pulling every horizontal
   * move up to the header. Geometry cannot express "the header is above the list", so the three
   * edges that matter are wired by hand. Everything else stays spatial.
   */
  const go = (target: string) => {
    if (doesFocusableExist(target)) void setFocus(target);
    return false;
  };
  const listTop = empty ? DRAWER_SEARCH_FOCUS : drawerRowKey(0);

  // The RTDB item already carries the localized label ("Tono +1" / "Key +1", docs/02 §2).
  const toneLabelOf = (item: QueueEntry) =>
    pickI18n(item.toneLabel, locale) || `${t('common:tone.label')} ${item.tone}`;

  const rowFor = (item: QueueEntry, index: number) => {
    const playing = item.status === 'playing';
    return (
      <QueueRow
        // The position is part of the React key on purpose. norigin registers a focusable under
        // the focusKey it mounted with and never re-keys it, so when the song in row 0 ended the
        // next one kept "drawer-row-1" while drawing as row 0: "drawer-row-0" no longer existed
        // and Down from "Agregar con QR" went nowhere (2026-10-26). A row that moves remounts.
        key={`${item.id}:${index}`}
        focusKey={drawerRowKey(index)}
        title={item.title}
        artists={formatArtists(item.artistNames, locale)}
        imageUrl={item.imageUrl}
        color={colorOf(item.songId)}
        toneLabel={toneLabelOf(item)}
        addedBy={{ name: item.addedBy.name, photo: item.addedBy.photo }}
        state={playing ? 'playing' : 'queued'}
        pending={actions.pendingId === item.id}
        onArrowPress={(direction) =>
          direction === 'up' && index === 0 ? go(DRAWER_QR_FOCUS) : true
        }
        playingLabel={t('tv:queue.nowPlaying').toUpperCase()}
        actions={
          playing
            ? []
            : [
                { key: 'play', label: t('tv:queue.play'), primary: true },
                { key: 'remove', label: t('tv:queue.remove') },
              ]
        }
        onAction={(key) => {
          if (key === 'play') actions.playNow(item.id, item.tone);
          else actions.remove(item.id);
        }}
        onFocus={() => onRowFocus(index)}
      />
    );
  };

  return (
    <FocusContext.Provider value={focusKey}>
      {/*
        UX-306: the drawer traps the focus exactly like a modal, so it is announced like one —
        `role="dialog"` + `aria-modal`, named by its own "Fila" heading and described by the
        count beside it. As a bare `role="region"` with no name a screen reader said nothing at
        all about what had just taken the screen.
      */}
      <div
        ref={ref}
        className={styles.drawer}
        role="dialog"
        aria-modal="true"
        aria-labelledby={DRAWER_TITLE_ID}
        aria-describedby={DRAWER_DESC_ID}
        data-focus-scope="queue-drawer"
        data-focus-default={empty ? DRAWER_SEARCH_FOCUS : DRAWER_QR_FOCUS}
        data-testid="queue-drawer"
      >
        <span className={styles.grabber} aria-hidden />
        <div className={styles.header}>
          <h2 className={styles.title} id={DRAWER_TITLE_ID}>
            {t('tv:queue.title')}
          </h2>
          <Tag tone="muted" className={styles.count} data-testid="drawer-count" id={DRAWER_DESC_ID}>
            {t('tv:queue.count', { count: queuedCount })}
          </Tag>
          <span className={styles.spacer} />
          <Button
            focusKey={DRAWER_QR_FOCUS}
            icon={<IconQr size={32} />}
            className={styles.qrButton}
            data-testid="drawer-qr"
            onSelect={onShowQr}
            onArrowPress={(direction) => (direction === 'down' ? go(listTop) : true)}
          >
            {t('tv:queue.addWithQr')}
          </Button>
        </div>

        {empty ? (
          <div className={styles.empty} data-testid="drawer-empty">
            <div className={styles.emptyQr}>
              <QrCode value={joinUrl} size={248} margin={1} />
            </div>
            <div className={styles.emptyText}>
              <div className={styles.emptyTitle}>{t('tv:queue.empty')}</div>
              <div className={styles.emptyBody}>{t('tv:queue.emptyHint')}</div>
              <div className={styles.emptyCode}>
                <span className={styles.emptyLink}>{GUEST_URL.replace('https://', '')}</span>
                <span>{' · '}</span>
                <span>
                  {t('common:session.code')} <b className={styles.emptyCodeValue}>{joinCode}</b>
                </span>
              </div>
              <div className={styles.emptyActions}>
                <Button
                  focusKey={DRAWER_SEARCH_FOCUS}
                  variant="primary"
                  className={styles.emptyButton}
                  data-testid="drawer-search"
                  onSelect={() => reset('search')}
                  onArrowPress={(direction) =>
                    direction === 'right'
                      ? go(DRAWER_CLOSE_FOCUS)
                      : direction === 'up'
                        ? go(DRAWER_QR_FOCUS)
                        : true
                  }
                >
                  {t('tv:queue.searchSongs')}
                </Button>
                <Button
                  focusKey={DRAWER_CLOSE_FOCUS}
                  className={styles.emptyButton}
                  data-testid="drawer-close"
                  onSelect={onClose}
                  onArrowPress={(direction) =>
                    direction === 'left'
                      ? go(DRAWER_SEARCH_FOCUS)
                      : direction === 'up'
                        ? go(DRAWER_QR_FOCUS)
                        : true
                  }
                >
                  {t('common:actions.close')}
                </Button>
              </div>
            </div>
          </div>
        ) : (
          <div className={styles.list}>
            <div className={cx(styles.track)} style={{ transform: `translateY(${-offset}px)` }}>
              {active.map(rowFor)}
              {played.length ? (
                <>
                  <div className={styles.playedTitle}>{t('tv:queue.played')}</div>
                  {played.map((item, index) => (
                    <QueueRow
                      key={`${item.id}:${index}`}
                      focusKey={drawerPlayedKey(index)}
                      title={item.title}
                      artists={formatArtists(item.artistNames, locale)}
                      imageUrl={item.imageUrl}
                      color={colorOf(item.songId)}
                      toneLabel={toneLabelOf(item)}
                      addedBy={{ name: item.addedBy.name, photo: item.addedBy.photo }}
                      state="played"
                      actions={[{ key: 'again', label: t('tv:queue.singAgain'), primary: true }]}
                      onAction={() => onSongAgain(songOfEntry(item))}
                      onFocus={() => onRowFocus(active.length + 1 + index)}
                    />
                  ))}
                </>
              ) : null}
            </div>
          </div>
        )}
      </div>
    </FocusContext.Provider>
  );
}
